import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Stethoscope, User, ArrowLeft, Save, XCircle } from 'lucide-react';

// Datos de ejemplo. En una aplicación real, obtendrías esto de tu API.
const mockPatientData = {
  '1': { name: 'Juan Pérez', age: 34, reason: 'Consulta General' },
  '2': { name: 'María García', age: 45, reason: 'Seguimiento de hipertensión' },
  '3': { name: 'Carlos López', age: 52, reason: 'Revisión de exámenes de laboratorio' },
  '4': { name: 'Ana Torres', age: 28, reason: 'Consulta de Primera Vez' },
};

const ClinicalEncounterPage = () => {
  const { appointmentId } = useParams();
  const patient = mockPatientData[appointmentId] || { name: 'Paciente Desconocido', age: 'N/A', reason: 'N/A' };

  const SectionTextarea = ({ label, placeholder }) => (
    <div className="mb-6">
      <label className="block text-sm font-bold text-gray-700 mb-2">{label}</label>
      <textarea
        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
        rows="4"
        placeholder={placeholder}
      ></textarea>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100 p-4 sm:p-8 font-sans">
      <div className="max-w-5xl mx-auto bg-white rounded-xl shadow-lg p-6 sm:p-8">
        {/* Encabezado */}
        <div className="flex flex-col sm:flex-row justify-between items-start border-b pb-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 flex items-center">
              <Stethoscope className="mr-3 text-blue-600" />
              Consulta Médica
            </h1>
            <div className="flex flex-wrap items-center text-gray-500 mt-2">
              <User size={16} className="mr-2" />
              <span className="font-semibold">{patient.name}</span>
              <span className="mx-2">•</span>
              <span>{patient.age} años</span>
              <span className="mx-2">•</span>
              <span>Motivo: {patient.reason}</span>
            </div>
          </div>
          <Link to="/dashboard" className="mt-4 sm:mt-0 flex items-center text-gray-500 hover:text-blue-600 transition-colors text-sm">
            <ArrowLeft size={16} className="mr-1" />
            Regresar al Dashboard
          </Link>
        </div>

        {/* Notas SOAP */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
          <div>
            <h2 className="text-xl font-semibold text-blue-700 mb-4 border-b-2 border-blue-200 pb-2">S - Subjetivo</h2>
            <SectionTextarea
              label="Relato del Paciente"
              placeholder="¿Qué siente el paciente? ¿Desde cuándo? ¿Cómo ha evolucionado?..."
            />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-green-700 mb-4 border-b-2 border-green-200 pb-2">O - Objetivo</h2>
            <SectionTextarea
              label="Hallazgos del Examen"
              placeholder="Signos vitales, inspección, palpación, auscultación..."
            />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-purple-700 mb-4 border-b-2 border-purple-200 pb-2">A - Análisis</h2>
            <SectionTextarea
              label="Diagnóstico o Impresión Diagnóstica"
              placeholder="Diagnóstico principal, diagnósticos diferenciales..."
            />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-orange-700 mb-4 border-b-2 border-orange-200 pb-2">P - Plan</h2>
            <SectionTextarea
              label="Plan de Tratamiento y Seguimiento"
              placeholder="Medicamentos, estudios de laboratorio/imagen, interconsultas, próxima cita..."
            />
          </div>
        </div>

        {/* Botones de Acción */}
        <div className="mt-8 pt-6 border-t flex justify-end space-x-4">
          <button className="flex items-center bg-gray-200 text-gray-700 px-6 py-2 rounded-lg font-semibold hover:bg-gray-300 transition-colors">
            <XCircle size={18} className="mr-2" />
            Cancelar
          </button>
          <button className="flex items-center bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-md">
            <Save size={18} className="mr-2" />
            Guardar y Finalizar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClinicalEncounterPage;